import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  JsonValue as AdminV2JsonValue,
  AdminV2RevenueAffiliateAttributionCreateResponse,
  AdminV2RevenueAffiliatePayoutCreateResponse,
  AdminV2RevenueAffiliatePayoutMarkPaidResponse,
  AdminV2RevenueAffiliatesResponse,
  AdminV2RevenueAffiliateUpsertResponse,
} from "@/types/adminV2";

export const buildAdminV2RevenueAffiliateEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2RevenueAffiliates: builder.query<
    AdminV2RevenueAffiliatesResponse,
    {
      organisationId?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/affiliates",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueAffiliates", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  upsertAdminV2RevenueAffiliate: builder.mutation<
    AdminV2RevenueAffiliateUpsertResponse,
    {
      affiliateId?: string;
      organisationId: string;
      affiliateCode: string;
      displayName: string;
      contactEmail?: string;
      payoutMethod?: string;
      payoutDetails?: AdminV2JsonValue;
      status?: "active" | "paused" | "blocked";
      isSelfReferralBlocked?: boolean;
      requiresManualReview?: boolean;
      riskFlags?: AdminV2JsonValue;
      notes?: string;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/revenue/affiliates",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueAffiliates", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  createAdminV2RevenueAffiliateAttribution: builder.mutation<
    AdminV2RevenueAffiliateAttributionCreateResponse,
    {
      affiliateId: string;
      organisationId: string;
      source?: string;
      referredEmail?: string;
      referredPersonId?: string;
      checkoutSessionId?: string;
      stripeCustomerId?: string;
      stripePaymentIntentId?: string;
      stripeSubscriptionId?: string;
      metadata?: AdminV2JsonValue;
    }
  >({
    query: ({ affiliateId, ...body }) => ({
      url: `/admin/v2/revenue/affiliates/${affiliateId}/attributions`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueAffiliates", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  createAdminV2RevenueAffiliatePayout: builder.mutation<
    AdminV2RevenueAffiliatePayoutCreateResponse,
    {
      affiliateId: string;
      organisationId: string;
      payoutPeriodStart?: string;
      payoutPeriodEnd?: string;
      amountCents: number;
      currency: string;
      status?: "pending" | "approved" | "void";
      transactionReference?: string;
      notes?: string;
      metadata?: AdminV2JsonValue;
    }
  >({
    query: ({ affiliateId, ...body }) => ({
      url: `/admin/v2/revenue/affiliates/${affiliateId}/payouts`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueAffiliates", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  markPaidAdminV2RevenueAffiliatePayout: builder.mutation<
    AdminV2RevenueAffiliatePayoutMarkPaidResponse,
    {
      affiliateId: string;
      payoutId: string;
      organisationId: string;
      paidAt?: string;
      transactionReference?: string;
      notes?: string;
      metadata?: AdminV2JsonValue;
    }
  >({
    query: ({ affiliateId, payoutId, ...body }) => ({
      url: `/admin/v2/revenue/affiliates/${affiliateId}/payouts/${payoutId}/mark-paid`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueAffiliates", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
